// Counter Intelligence — scoped bulk item delete ("delete everything I added today").
//
// This is the OTHER kind of item delete, and keeping it separate from the look-alike
// dedup scan is the whole point of the module.
//
//   dedup  ("find my duplicate items")      → compare NAMES, group variants, review.
//   scoped ("delete the items I added today") → filter by created_at. Names are never
//                                               compared; the date IS the selection.
//
// The created_at window is not only whole days: "in the last hour", "this morning",
// "since 8am", "between 2 and 4pm" resolve to sub-day instants (parseTimeWindow), and
// wording that points at the import EVENT rather than any clock — "the recent
// import", "my last upload", "what I just added" — resolves against the store's
// actual latest burst of item creations (matchLatestImportDelete → the server's
// latest_batch scan), which then SAYS what window it found before anything is ticked.
//
// They used to share one path: the delete command was handed to the dedup scan as a
// free-text "query", matched no item name, and the scan fell back to showing every
// look-alike group in the catalog. "Delete all the items added today" therefore
// offered 60 sets / 151 items that had nothing to do with today. The scope below is a
// HARD filter — if we can't resolve a date, we say so rather than scanning something
// else and calling it an answer.
//
// Pure (no React / no I/O) so it is unit-testable.

import { parseDateRanges } from './ciReportQuery.mjs';

// Verbs that mean "take these out of my catalog". "Find"/"show"/"scan" are absent on
// purpose — those are dedup review verbs and must not trigger a delete scope.
const DELETE_VERB_RE = /\b(delete|deleting|remove|removing|archive|archiving|get rid of|getting rid of|clear out|clean out|wipe|purge|undo|revert|roll ?back)\b/i;

// What is being deleted. Broad, because people say "delete everything I added today"
// as readily as "delete the items I added today".
const ITEM_NOUN_RE = /\b(items?|products?|ingredients?|entries|entry|rows?|everything|all of it|import|imports|upload|uploads|batch|list)\b/i;

// The relationship between the items and the date: they were ADDED then. Without one
// of these (or an explicit "on <date>") a bare date is likelier to be about a count or
// an order than about when items were created.
const ADDED_RE = /\b(added|add|created|create|imported|import|importing|uploaded|upload|entered|put in|brought in|made|new|from)\b/i;

// A date phrase introduced by a preposition — "delete the items on July 28",
// "everything from yesterday", "all the items between 2 and 4pm" — covers the
// phrasings that skip the "added" verb. "between" is safe here because a phrase
// that resolves no date/time window still falls through to the other matchers.
const DATED_PREP_RE = /\b(on|from|since|dated|during|between)\b/i;

// A trailing window measured back from now — "the past 3 days", "the last two
// weeks", "2 days ago", "the last hour", "45 minutes ago". Unlike a bare "today"
// (which can be the time to ACT: "remove all the beer items today"), this wording
// can only describe when the items came in, so it is a date signal on its own,
// with no "added" verb or preposition required: "delete all items in inventory
// for the past 2 days". Part-of-day words ("this morning") are deliberately NOT
// here — like bare "today" they can name the time to act, so they need an
// "added" verb or a preposition to count as a window.
const DURATION_RANGE_RE = /\b(?:past|last|previous|prior)\s+(?:\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|couple(?:\s+of)?|few)\s+(?:days?|weeks?|months?)\b|\b(?:past|previous)\s+(?:week|month)\b|\bdays?\s+ago\b|\b(?:past|last|previous|prior)\s+(?:(?:\d{1,3}|an?|one|two|three|four|five|six|seven|eight|nine|ten|couple(?:\s+of)?|few)\s+)?(?:hours?|hrs?|minutes?|mins?)\b|\b(?:past|last|previous|prior)\s+half(?:\s+an?)?[-\s]?hour\b|\b(?:hours?|hrs?|minutes?|mins?)\s+ago\b|\bhalf\s+an?\s+hour\s+ago\b/i;

// Duplicate wording hands the request back to the dedup flow even when it also carries
// a delete verb: "remove the duplicates" is a look-alike review, not a date sweep.
const DUPLICATE_TOPIC_RE = /\b(duplicat\w*|dupes?|dups?|look-?alikes?|de-?dup\w*|dedup\w*|copies|clutter)\b/i;

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Local-day bounds as absolute instants. created_at is a timestamptz, and "today"
// means the user's day — so the window is computed from the USER'S clock here and sent
// to the server as instants, rather than letting the server guess a timezone.
const startOfLocalDay = (isoDay) => {
  const [y, m, d] = String(isoDay).split('-').map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};
const endOfLocalDay = (isoDay) => {
  const [y, m, d] = String(isoDay).split('-').map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d, 23, 59, 59, 999);
};

// ── Sub-day time windows ("the last hour", "this morning", "since 8am") ──────
//
// The server filter is already instant-based (created_from/created_to ISO
// timestamps), so day granularity was only ever a limit of the PARSING. This
// parser resolves clock-level wording into the same scope shape the day parser
// produces — computed from the user's own clock, like the day bounds above.
//
// "N hours ago" resolves to the SINCE-window [now − N, now], not a slice around
// that instant: the batch the user is pointing at can then never be missed, and
// anything newer that rode into the window is visible in the review (nothing is
// archived without being ticked). The label says exactly what is shown ("in the
// last 2 hours"), so the generosity is never silent.

const TIME_WORD_COUNTS = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10, couple: 2, few: 3,
};
const TIME_COUNT_PATTERN = '\\d{1,3}|an?|one|two|three|four|five|six|seven|eight|nine|ten|couple(?:\\s+of)?|few';
const parseTimeCount = (raw) => {
  const t = String(raw || '').toLowerCase().replace(/\s+of$/, '').trim();
  const n = /^\d+$/.test(t) ? parseInt(t, 10) : TIME_WORD_COUNTS[t];
  return Number.isInteger(n) && n >= 1 && n <= 100 ? n : null;
};

// One clock mention: "8am", "8:30 pm", "12 p.m.", "14:30", "noon", "midnight".
// Four capture groups per use: hour, minutes, meridiem, word.
const CLOCK_SRC = '(?:(\\d{1,2})(?::(\\d{2}))?\\s*(a\\.?m\\.?|p\\.?m\\.?)?|(noon|midnight))';

// Resolve one matched clock into 24h parts. A bare number is accepted only when
// the phrasing supplies a meridiem to inherit ("between 2 and 4pm" — the 2
// borrows the pm) or it is unambiguous 24h form ("14:30"); a lone "since 8" is
// refused rather than guessed.
const clockFromMatch = ({ hour, minutes, meridiem, word }, inheritMeridiem = null) => {
  if (word) return String(word).toLowerCase() === 'noon' ? { h: 12, m: 0 } : { h: 0, m: 0 };
  if (!hour) return null;
  const rawH = parseInt(hour, 10);
  const m = minutes ? parseInt(minutes, 10) : 0;
  if (!(m >= 0 && m <= 59)) return null;
  if (!(rawH >= 1 && rawH <= 12)) {
    // 24h form ("14:30") — minutes present, no meridiem.
    if (!meridiem && minutes && rawH >= 0 && rawH <= 23) return { h: rawH, m };
    return null;
  }
  const mer = String(meridiem || inheritMeridiem || '').toLowerCase();
  if (!mer) return null;
  let h = rawH % 12;
  if (mer.startsWith('p')) h += 12;
  return { h, m };
};

const formatClockParts = (h, m) => {
  const mer = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${mer}`;
};
const formatClockOf = (date) => formatClockParts(date.getHours(), date.getMinutes());

const dayAnchor = (now, { yesterday = false } = {}) =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate() - (yesterday ? 1 : 0));
const atTime = (day, h, m = 0, s = 0, ms = 0) =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m, s, ms);
const sameLocalDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const localIsoDay = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// The same scope shape matchScopedItemDelete returns for a day range, so every
// consumer (preview, messages, card) treats both without branching. timeWindow
// marks it sub-day so the review rows can show clock times, not just dates.
const timeWindowScope = (from, to, label, scopeLabel) => ({
  kind: 'created_date',
  createdFrom: from.toISOString(),
  createdTo: to.toISOString(),
  startDate: localIsoDay(from),
  endDate: localIsoDay(to),
  label,
  scopeLabel,
  assumed: false,
  timeWindow: true,
});

/**
 * Resolve sub-day time wording into a created_at window, or null when the text
 * carries none. Handled: "the last/past N hours|minutes", "the last half hour",
 * "N hours/minutes ago", "earlier today", "last night", "tonight",
 * "this/yesterday morning|afternoon|evening", "since/after 8am",
 * "between 2 and 4pm" / "from 2pm to 4pm". Day-only wording ("yesterday",
 * "July 28th") deliberately returns null — parseDateRanges owns days.
 */
export const parseTimeWindow = (raw, now = new Date()) => {
  const text = String(raw || '').toLowerCase();
  if (!text.trim()) return null;
  // "yesterday" anywhere in the phrase anchors clock windows to yesterday
  // ("since 8am yesterday", "yesterday between 2 and 4pm").
  const yesterdayAnchor = /\byesterday\b/.test(text);

  // "between 2 and 4pm", "from 2pm to 4pm", "between noon and 2:30pm".
  let m = text.match(new RegExp(`\\b(?:between|from)\\s+${CLOCK_SRC}\\s+(?:and|to|until|till|through|[–—-])\\s+${CLOCK_SRC}\\b`));
  if (m) {
    const first = { hour: m[1], minutes: m[2], meridiem: m[3], word: m[4] };
    const second = { hour: m[5], minutes: m[6], meridiem: m[7], word: m[8] };
    let a = clockFromMatch(first, m[7] || null);
    let b = clockFromMatch(second, m[3] || null);
    if (a && b) {
      // An inherited meridiem can put the start after the end ("between 11 and
      // 2pm" reads 23:00 → 14:00) — the natural reading flips the bare side.
      if (a.h * 60 + a.m > b.h * 60 + b.m) {
        if (!first.meridiem && !first.word) a = { h: (a.h + 12) % 24, m: a.m };
        else if (!second.meridiem && !second.word) b = { h: (b.h + 12) % 24, m: b.m };
      }
      if (a.h * 60 + a.m <= b.h * 60 + b.m) {
        let day = dayAnchor(now, { yesterday: yesterdayAnchor });
        // A window that hasn't started today means yesterday's ("between 9 and
        // 10pm" said at noon).
        if (!yesterdayAnchor && atTime(day, a.h, a.m).getTime() > now.getTime()) {
          day = dayAnchor(now, { yesterday: true });
        }
        const from = atTime(day, a.h, a.m);
        const to = atTime(day, b.h, b.m, 59, 999);
        const dayWord = sameLocalDay(day, now) ? 'today' : 'yesterday';
        const label = `between ${formatClockParts(a.h, a.m)} and ${formatClockParts(b.h, b.m)} ${dayWord}`;
        return timeWindowScope(from, to, label, label);
      }
    }
  }

  // "since 8am", "after 2:30pm", "since noon" — open-ended up to now.
  m = text.match(new RegExp(`\\b(?:since|after)\\s+${CLOCK_SRC}\\b`));
  if (m) {
    const c = clockFromMatch({ hour: m[1], minutes: m[2], meridiem: m[3], word: m[4] });
    if (c) {
      let day = dayAnchor(now, { yesterday: yesterdayAnchor });
      if (!yesterdayAnchor && atTime(day, c.h, c.m).getTime() > now.getTime()) {
        day = dayAnchor(now, { yesterday: true });
      }
      const from = atTime(day, c.h, c.m);
      const suffix = sameLocalDay(day, now) ? '' : ' yesterday';
      const label = `since ${formatClockParts(c.h, c.m)}${suffix}`;
      return timeWindowScope(from, now, label, label);
    }
  }

  // "the last hour", "the past 2 hours", "last 45 minutes", "past couple of hours".
  m = text.match(new RegExp(`\\b(?:past|last|previous|prior)\\s+(?:(${TIME_COUNT_PATTERN})\\s+)?(hours?|hrs?|minutes?|mins?)\\b`));
  if (m) {
    const n = m[1] ? parseTimeCount(m[1]) : 1;
    if (n) {
      const isHours = /^h/.test(m[2]);
      const unit = isHours ? 'hour' : 'minute';
      const from = new Date(now.getTime() - n * (isHours ? 3600000 : 60000));
      const label = `the last ${n === 1 ? unit : `${n} ${unit}s`}`;
      return timeWindowScope(from, now, label, `in ${label}`);
    }
  }
  if (/\b(?:past|last|previous|prior)\s+half(?:\s+an?)?[-\s]?hour\b/.test(text)) {
    return timeWindowScope(new Date(now.getTime() - 1800000), now, 'the last half hour', 'in the last half hour');
  }

  // "2 hours ago", "an hour ago", "30 minutes ago", "half an hour ago" — the
  // since-window described in the header comment.
  m = text.match(new RegExp(`\\b(${TIME_COUNT_PATTERN})\\s+(hours?|hrs?|minutes?|mins?)\\s+ago\\b`));
  if (m) {
    const n = parseTimeCount(m[1]);
    if (n) {
      const isHours = /^h/.test(m[2]);
      const unit = isHours ? 'hour' : 'minute';
      const from = new Date(now.getTime() - n * (isHours ? 3600000 : 60000));
      const label = `the last ${n === 1 ? unit : `${n} ${unit}s`}`;
      return timeWindowScope(from, now, label, `in ${label}`);
    }
  }
  if (/\bhalf\s+an?\s+hour\s+ago\b/.test(text)) {
    return timeWindowScope(new Date(now.getTime() - 1800000), now, 'the last half hour', 'in the last half hour');
  }

  if (/\bearlier\s+today\b/.test(text)) {
    return timeWindowScope(atTime(dayAnchor(now), 0, 0), now, 'earlier today', 'earlier today');
  }

  // "last night" — yesterday evening through the small hours of today.
  if (/\blast\s+night\b/.test(text)) {
    const y = dayAnchor(now, { yesterday: true });
    return timeWindowScope(atTime(y, 17, 0), atTime(dayAnchor(now), 4, 0), 'last night', 'last night');
  }
  if (/\btonight\b/.test(text)) {
    const day = dayAnchor(now);
    return timeWindowScope(atTime(day, 17, 0), atTime(day, 23, 59, 59, 999), 'tonight', 'tonight');
  }

  // "this morning", "yesterday afternoon", "the evening". A qualifier is
  // required — a bare "morning" is likelier a greeting than a window.
  m = text.match(/\b(?:(yesterday|this|the)\s+)(morning|afternoon|evening)\b/);
  if (m) {
    const yest = m[1] === 'yesterday' || yesterdayAnchor;
    const day = dayAnchor(now, { yesterday: yest });
    const part = m[2];
    const from = part === 'morning' ? atTime(day, 0, 0) : part === 'afternoon' ? atTime(day, 12, 0) : atTime(day, 17, 0);
    const to = part === 'morning' ? atTime(day, 12, 0) : part === 'afternoon' ? atTime(day, 18, 0) : atTime(day, 23, 59, 59, 999);
    const label = `${yest ? 'yesterday' : 'this'} ${part}`;
    return timeWindowScope(from, to, label, label);
  }

  return null;
};

// A phrase that reads naturally after the word "added", so every message about the
// scope can state exactly what was matched ("I found 121 items added on July 28, 2026").
export const describeCreatedScope = (range) => {
  if (!range) return '';
  const label = String(range.label || '').trim();
  if (!label) return '';
  if (/^(today|yesterday)$/i.test(label)) return label.toLowerCase();
  if (/\bago$/i.test(label)) return label; // "added 3 days ago", not "on 3 days ago"
  if (/^this\b/i.test(label)) return label; // "added this week"
  if (range.kind === 'day') return `on ${label}`;
  if (range.kind === 'days') return `in ${label}`;
  return `in ${label}`;
};

/**
 * Detect "delete the items added <when>" and resolve the created_at window.
 *
 * @returns null when this is not a date-scoped delete, otherwise
 *   { kind, createdFrom, createdTo, startDate, endDate, label, scopeLabel, assumed }
 *   where createdFrom/createdTo are absolute ISO instants for the server filter.
 */
export const matchScopedItemDelete = (raw, { now = new Date() } = {}) => {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (!DELETE_VERB_RE.test(text)) return null;
  if (DUPLICATE_TOPIC_RE.test(text)) return null;  // a look-alike review, not a sweep
  if (!ITEM_NOUN_RE.test(text)) return null;
  if (!ADDED_RE.test(text) && !DATED_PREP_RE.test(text) && !DURATION_RANGE_RE.test(text)) return null;

  const ranges = parseDateRanges(text, now);

  // Clock-level wording resolves first — "yesterday morning" must be yesterday's
  // MORNING, not the whole day the day parser would read. But when the text also
  // names a calendar day the time window isn't anchored to ("added July 28th
  // between 2 and 4pm" — the clock window assumes today), the DAY wins as the
  // honest superset: the review shows the whole day rather than silently showing
  // the wrong one.
  const tw = parseTimeWindow(text, now);
  if (tw && Date.parse(tw.createdFrom) <= now.getTime()) {
    const containedInDay = !ranges.length
      || (ranges.length === 1 && ranges[0].startDate <= tw.startDate && ranges[0].endDate >= tw.endDate);
    if (containedInDay) return tw;
  }

  if (!ranges.length) return null;
  // Deleting is destructive, so an ambiguous "August or September?" is never resolved
  // by guessing — the caller asks instead.
  if (ranges.length > 1) {
    return { kind: 'ambiguous_date', ranges };
  }

  const range = ranges[0];
  const from = startOfLocalDay(range.startDate);
  const to = endOfLocalDay(range.endDate);
  // A window that hasn't happened yet means we misread the wording; better to ask.
  if (from.getTime() > now.getTime()) return null;

  return {
    kind: 'created_date',
    createdFrom: from.toISOString(),
    createdTo: to.toISOString(),
    startDate: range.startDate,
    endDate: range.endDate,
    label: range.label,
    scopeLabel: describeCreatedScope(range),
    assumed: !!range.assumed,
  };
};

/**
 * Build the same scope from the classifier's extracted date phrase, for wordings the
 * deterministic matcher above misses ("clear out what came in on the 28th of July").
 * The phrase alone is parsed, so surrounding words can't drag in a stray date.
 */
export const scopeFromDatePhrase = (phrase, { now = new Date() } = {}) => {
  const text = String(phrase || '').trim();
  if (!text) return null;
  const ranges = parseDateRanges(text, now);
  // Clock wording first, same day-containment rule as the sentence matcher.
  const tw = parseTimeWindow(text, now);
  if (tw && Date.parse(tw.createdFrom) <= now.getTime()) {
    const containedInDay = !ranges.length
      || (ranges.length === 1 && ranges[0].startDate <= tw.startDate && ranges[0].endDate >= tw.endDate);
    if (containedInDay) return tw;
  }
  if (ranges.length !== 1) return null;
  const range = ranges[0];
  const from = startOfLocalDay(range.startDate);
  const to = endOfLocalDay(range.endDate);
  if (from.getTime() > now.getTime()) return null;
  return {
    kind: 'created_date',
    createdFrom: from.toISOString(),
    createdTo: to.toISOString(),
    startDate: range.startDate,
    endDate: range.endDate,
    label: range.label,
    scopeLabel: describeCreatedScope(range),
    assumed: !!range.assumed,
  };
};

// ── Latest-import references ("the recent import", "what I just added") ──────
//
// Wording that points at the import EVENT rather than any clock or calendar. No
// deterministic window can resolve "the recent import" — the last import might
// have been ten minutes or ten days ago — so these route to the server's
// latest_batch scan, which finds the store's actual most recent burst of item
// creations and reports the real window it found. That answer IS the discussion:
// the assistant states how it read the fuzzy reference before anything is ticked.
//
// The boundary against real scope names matters: "Asia Imports" is a vendor and
// must stay one. Every pattern here requires recency wording (recent/latest/
// last/newest), a definite singular event ("the import" — never "the Imports
// category"), a "just <verb>" form, or a moments-ago phrase; a proper name in
// front of "Imports" matches none of them.

// "the recent import", "my last upload", "the newest batch of items".
const RECENT_EVENT_RE = /\b(?:most\s+recent|recent|latest|last|newest)\s+(?:import|imports|upload|uploads|batch|batches)\b/i;
// "the import", "that upload" — a definite reference to ONE event. Singular
// only, and never when a kind word follows ("the Import category" is a name).
const DEFINITE_EVENT_RE = /\b(?:the|that|this)\s+(?:import|upload)\b(?!\s*(?:category|categories|vendor|vendors|suppliers?|section|list|lists|area|location))/i;
// "what I just imported", "the items I just added".
const JUST_VERB_RE = /\bjust\s+(?:imported|uploaded|added|entered|brought\s+in|put\s+in)\b/i;
// "just now", "a minute ago", "moments ago", "recently" — recency with no
// usable quantity. Quantified forms ("5 minutes ago") are claimed first by
// parseTimeWindow wherever both run.
const MOMENTS_AGO_RE = /\b(?:just\s+now|recently|moments?\s+ago|seconds?\s+ago|(?:a|one)\s+(?:minute|moment|second)\s+ago|(?:a\s+)?(?:few|couple(?:\s+of)?)\s+(?:minutes|moments|seconds)\s+ago|minutes\s+ago)\b/i;

// True when the text (a whole request or a classifier-extracted phrase) refers
// to the latest import/upload rather than naming a scope or a clock time.
export const matchesLatestImportWording = (raw) => {
  const text = String(raw || '');
  if (!text.trim()) return false;
  return RECENT_EVENT_RE.test(text) || DEFINITE_EVENT_RE.test(text)
    || JUST_VERB_RE.test(text) || MOMENTS_AGO_RE.test(text);
};

/**
 * Detect "delete the items from the recent import" / "undo my last upload" /
 * "get rid of what I just added". Returns { kind: 'latest_batch' } for the
 * drawer to run the server-resolved latest-batch preview, or null.
 * Runs AFTER the date/time matcher (an explicit date beats "last batch" wording:
 * "delete the last batch I added on July 28" is a July 28 sweep) and after the
 * attribute matcher, which refuses these texts via matchesLatestImportWording.
 */
export const matchLatestImportDelete = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (!DELETE_VERB_RE.test(text)) return null;
  if (DUPLICATE_TOPIC_RE.test(text)) return null;  // "remove the duplicates I just added" — dedup's
  if (!matchesLatestImportWording(text)) return null;
  return { kind: 'latest_batch' };
};

// Shown when the wording names two dates — the user picks rather than us guessing.
export const ambiguousDateMessage = (ranges) => {
  const labels = (Array.isArray(ranges) ? ranges : []).map((r) => r.label).filter(Boolean);
  if (labels.length < 2) return 'Which date do you mean? Tell me the day the items were added.';
  return `You named more than one date (${labels.join(' and ')}). Which one were the items added on? I won't delete anything until you tell me.`;
};

// ── Attribute-scoped delete ("delete all the Asia Imports items") ────────────
//
// The third kind of item delete: a batch picked by WHO the items belong to — one
// vendor, category, or storage area. This matcher only extracts the scope PHRASE
// ("Asia Imports"); what KIND of scope it is gets resolved server-side against
// the store's actual values, so the user never has to say the word "vendor".

// Frames that pin the scope phrase X, each requiring a TOTALITY word — "remove
// the item from the Walk-In" is a template edit of one item, not a batch delete,
// so a bare "the item(s)" never matches. A trailing kind word ("the Beer
// category") and a trailing "please" are stripped from the phrase.
const ATTRIBUTE_SCOPE_FRAMES = [
  // "everything in the Walk-In", "all of the items from Asia Imports",
  // "every item in the Beer category", "all from Sysco".
  /\b(?:everything|every\s+(?:item|product)|all(?:\s+of)?(?:\s+the)?(?:\s+(?:items|products))?)\s+(?:in|from|under)\s+(?:the\s+)?(.+?)(?:\s+(?:category|categories|vendor|vendors|suppliers?|storage\s+area|location|section))?(?:\s+please)?\s*[.!?]*$/i,
  // "all the Asia Imports items", "all of the Beer items".
  /\ball\s+(?:of\s+)?(?:the\s+)?(.+?)\s+(?:items|products)(?:\s+please)?\s*[.!?]*$/i,
];

// A phrase that is only a possessive/qualifier means the whole catalog ("delete
// all my items", "archive all the discontinued items") — that stays refused, so
// it must not become a scope search for the word "my".
const ATTRIBUTE_SCOPE_STOP_RE = /^(?:my|our|your|the|these|those|them|all|every|each|any|some|old|new|discontinued)$/i;
// A phrase that itself talks about items/catalogs is also a whole-catalog
// wording ("my item list", "the catalog"), not a scope. `inven\w*` covers
// "inventory" plus its common misspellings ("invenotry", "inventroy") — a
// typo'd catalog word must not turn into a vendor-name search.
const ATTRIBUTE_SCOPE_ITEMS_RE = /\b(?:items?|products?|everything|catalog(?:ue)?|inven\w*|list)\b/i;

// A creation verb inside the phrase means the wording is about WHEN the items
// arrived, not a vendor/category/area name ("this store invenotry added in the
// past 3 dayz", typos and all). Such a phrase falls through to the model
// classifier for an open reading — it is never searched as a scope name.
const ATTRIBUTE_SCOPE_ADDED_RE = /\b(?:added|created|imported|uploaded|entered|put in|brought in)\b/i;

// A scope phrase can name SEVERAL scopes at once: "Asia Imports and Shamrock
// Foods", "Beer, Wine, and Liquor", "Walk-In & Freezer". Split only on the clear
// separators (and / & / commas, Oxford comma included) — a vendor literally
// named "X and Y" is the rare loser here, and the resolver falls back to the
// whole phrase when a piece fails to resolve but the whole phrase resolves.
// Each piece also drops a repeated lead-in ("shamrock food and FROM asia import",
// "Beer, IN Wine") — the preposition belongs to the sentence, not the name.
export const splitScopePhrases = (phrase) => {
  const text = String(phrase || '').trim();
  if (!text) return [];
  const parts = text
    .replace(/,\s*and\s+/gi, ', ')   // Oxford comma: "X, Y, and Z" → "X, Y, Z"
    .replace(/\s*&\s*/g, ' and ')    // "X & Y" reads the same as "X and Y"
    .split(/\s*,\s*|\s+and\s+/i)
    .map((p) => p.trim().replace(/^(?:from|in|under)\s+(?:the\s+)?/i, '').trim())
    .filter(Boolean);
  return parts.length ? parts : [text];
};

// How each scope kind reads in a label: preposition + noun.
const SCOPE_FIELD_PHRASE = {
  vendor: ['from', 'vendor'],
  category: ['in', 'category'],
  location: ['in', 'storage area'],
};

// The label for one or MORE resolved scope filters, reading after "items":
// one filter → "from vendor Asia Imports"; two of one kind → "from vendor Asia
// Imports and Shamrock Foods" (the kind word is said once); mixed kinds keep
// their own prepositions ("from vendor Sysco and in category Beer"). Mirrors
// attributeScopeLabel/attributeScopeListLabel in items-ai-cleanup/core.mjs.
export const attributeScopeListLabel = (filters) => {
  const list = (Array.isArray(filters) ? filters : []).filter((f) => f && f.field && f.value);
  if (!list.length) return '';
  const byField = [];
  for (const f of list) {
    const value = String(f.value).trim();
    if (!value) continue;
    const last = byField[byField.length - 1];
    if (last && last.field === f.field) last.values.push(value);
    else byField.push({ field: f.field, values: [value] });
  }
  return byField
    .map(({ field, values }) => {
      const [prep, noun] = SCOPE_FIELD_PHRASE[field] || ['matching', ''];
      return noun ? `${prep} ${noun} ${values.join(' and ')}` : values.join(' and ');
    })
    .join(' and ');
};

/**
 * Detect "delete all the <X> items" / "delete everything in the <X>" and extract
 * the scope phrase X. Returns null for date-scoped deletes (matchScopedItemDelete
 * owns those), single-item deletes, whole-catalog deletes, and duplicate cleanups.
 *
 * @returns null when this is not an attribute-scoped delete, otherwise
 *   { kind: 'attribute_scope', phrase, phrases } — the raw phrase for messages,
 *   and the phrase split into separately-named scopes ("X and Y" → [X, Y]) for
 *   the server-side resolver.
 */
export const matchAttributeScopedItemDelete = (raw, { now = new Date() } = {}) => {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (!DELETE_VERB_RE.test(text)) return null;
  if (DUPLICATE_TOPIC_RE.test(text)) return null;  // a look-alike review, not a sweep
  // "delete everything from the recent import" fits the attribute frames
  // perfectly — and searching vendors/categories/areas for a name like "recent
  // import" is exactly the failure this guard exists for. Latest-import wording
  // belongs to matchLatestImportDelete, which the drawer runs right after this.
  if (matchesLatestImportWording(text)) return null;
  for (const frame of ATTRIBUTE_SCOPE_FRAMES) {
    const m = text.match(frame);
    if (!m) continue;
    const phrase = String(m[1] || '')
      .trim()
      .replace(/^["']+|["']+$/g, '')
      .replace(/\s+/g, ' ');
    if (!phrase) continue;
    if (ATTRIBUTE_SCOPE_STOP_RE.test(phrase)) return null;   // "all my items" — whole catalog
    if (ATTRIBUTE_SCOPE_ITEMS_RE.test(phrase)) return null;  // "my item list" — whole catalog
    if (ATTRIBUTE_SCOPE_ADDED_RE.test(phrase)) return null;  // date-ish wording — classifier's call
    // A scope that parses as a date or a time window belongs to the created-date
    // sweep above ("everything from yesterday", "everything from this morning"),
    // which runs first in the drawer — the attribute scan must never claim it.
    if (parseDateRanges(phrase, now).length) return null;
    if (parseTimeWindow(phrase, now)) return null;
    return { kind: 'attribute_scope', phrase, phrases: splitScopePhrases(phrase) };
  }
  return null;
};

// ── Intent confirmation for an attribute-scoped delete ───────────────────────
//
// A scope phrase can mean more than one thing: "freezer" is a storage area,
// smells like the frozen category, and may live inside item names. Auto-picking
// the first reading handed the user a delete list they never agreed to (the
// live case: "delete all items in the freezer" listed frozen-food items without
// ever SAYING what "freezer" was read as). So when resolve_scope reports the
// phrase's possible readings, only a single exact vendor/category/location
// reading goes straight to the review card — anything else asks FIRST (intent
// pass), and the scan waits for the answer (list pass). Nothing is archived
// until both passes confirm.

// The pending context while the intent question is open. `asked_at` stamps when
// the question was posed — this module is pure, so expiry is the drawer's call.
export const buildDeleteScopePending = ({ phrase, phrases, interpretations, storeId } = {}) => ({
  kind: 'pending_delete_scope',
  awaiting: 'intent',
  phrase: String(phrase || '').trim(),
  phrases: Array.isArray(phrases) ? phrases : [],
  interpretations: Array.isArray(interpretations) ? interpretations : [],
  storeId: storeId ?? null,
  asked_at: Date.now(),
});

// The label of one reading's chip says what picking it will DO ("Show …") and
// carries the item count, so the choice shows its blast radius up front.
const deleteScopeChipLabel = (interp) => `Show ${interp.label} (${interp.item_count})`;

// One chip per reading (best first — resolve_scope ranks them) plus an explicit
// opt-out, so "none of these" is a tap rather than something the user must phrase.
export const deleteScopeChips = (pending) => {
  const interpretations = Array.isArray(pending?.interpretations) ? pending.interpretations : [];
  return [
    ...interpretations.map((interp, i) => ({ key: `dscope:${i}`, label: deleteScopeChipLabel(interp) })),
    { key: 'dscope:none', label: 'None of these' },
  ];
};

// The intent-pass question. One reading → state it and check ("Is that the
// group you mean?"); several → ask which. Both end by saying nothing has been
// changed — the user must never wonder whether the question itself deleted
// something.
export const intentConfirmMessage = (phrase, interpretations) => {
  const list = (Array.isArray(interpretations) ? interpretations : []).filter((i) => i && i.label);
  const quoted = `“${String(phrase || '').trim()}”`;
  const tail = ' Nothing has been changed yet.';
  if (list.length === 1) {
    return `I read ${quoted} as ${list[0].label} — ${list[0].item_count} items. Is that the group you mean?${tail}`;
  }
  return `I found a few things ${quoted} could mean. Which one should I pull up?${tail}`;
};

// "Yes" settles for the TOP reading (the resolver ranks best-first). Anchored
// to the whole reply so a "yes" inside a longer sentence can't confirm a delete.
const DELETE_SCOPE_YES_RE = /^(?:yes|yeah|yep|yup|sure|ok(?:ay)?|correct|right|exactly|that['’]?s (?:right|correct|it)|go ahead|do it|confirm|sounds good)[.!]*$/i;
const DELETE_SCOPE_CANCEL_RE = /^(?:no|nope|nah|none|none of (?:these|them)|cancel|never ?mind|stop|forget it|not now|no thanks)[.!]*$/i;

// "the first one" / "option 2" — an ordinal points into the ranked list.
const DELETE_SCOPE_ORDINAL_INDEX = { first: 0, second: 1, third: 2, fourth: 3, fifth: 4 };

// "the category one", "the storage area one", "the item names one" — a field
// word picks the first reading of that kind. Checked in this order so "storage
// area" lands on location before a bare "area", and "item names" on description.
const DELETE_SCOPE_FIELD_WORDS = [
  [/\b(?:storage\s+area|location|area)\b/i, 'location'],
  [/\bcategor\w*/i, 'category'],
  [/\b(?:vendor|supplier)\w*/i, 'vendor'],
  [/\b(?:item\s+names?|names?|description|containing)\b/i, 'description'],
];

/**
 * Resolve the user's answer to the intent question.
 *
 * @returns { action: 'confirm', interpretation } when a reading was picked,
 *   { action: 'cancel' } when they backed out, or null when the reply doesn't
 *   settle the question — the caller re-asks rather than guessing, because the
 *   next step is a delete.
 */
export const resolveDeleteScopeReply = (text, pending) => {
  const t = String(text || '').trim();
  const interpretations = Array.isArray(pending?.interpretations) ? pending.interpretations : [];
  if (!t || !interpretations.length) return null;
  const lower = t.toLowerCase().replace(/\s+/g, ' ');
  // Picks run BEFORE the cancel words: "no, the category one" is a correction,
  // not a cancel — same rule the report follow-ups use.
  // Chips send their label as the reply, so an exact label match is a pick.
  for (let i = 0; i < interpretations.length; i += 1) {
    if (lower === deleteScopeChipLabel(interpretations[i]).toLowerCase()) {
      return { action: 'confirm', interpretation: interpretations[i] };
    }
  }
  // An ordinal past the end of the list means we misread — re-ask, don't clamp.
  const ordWord = lower.match(/\b(first|second|third|fourth|fifth)\b/);
  const ordNum = lower.match(/^(?:option\s+|number\s+|#)?([1-9])$/);
  const ordIndex = ordWord ? DELETE_SCOPE_ORDINAL_INDEX[ordWord[1]] : ordNum ? parseInt(ordNum[1], 10) - 1 : -1;
  if (ordIndex >= 0) {
    return ordIndex < interpretations.length
      ? { action: 'confirm', interpretation: interpretations[ordIndex] }
      : null;
  }
  for (const [re, field] of DELETE_SCOPE_FIELD_WORDS) {
    if (!re.test(lower)) continue;
    const found = interpretations.find((i) => i && i.field === field);
    if (found) return { action: 'confirm', interpretation: found };
  }
  if (DELETE_SCOPE_YES_RE.test(lower)) return { action: 'confirm', interpretation: interpretations[0] };
  if (DELETE_SCOPE_CANCEL_RE.test(lower)) return { action: 'cancel' };
  return null;
};

// The note appended when a year had to be inferred ("July 28th" in July 2026), so a
// wrong assumption is visible BEFORE anything is archived rather than after.
export const assumedYearNote = (range) => {
  if (!range || !range.assumed) return '';
  return ` I read that as ${range.label} — say the year if you meant a different one.`;
};

export const SCOPED_DELETE_HINT =
  'These are the items added in that window, grouped by vendor — not look-alike matches. Ticking them archives them: they drop off upcoming counts, your past counts, orders, and reports keep them, and you can restore them from the archive.';

// The same hint for an attribute-scoped review (one vendor/category/storage
// area) — "added in that window" would describe a date batch, not this one.
export const ATTRIBUTE_SCOPED_DELETE_HINT =
  'These are the items in that group — not look-alike matches. Ticking them archives them: they drop off upcoming counts, your past counts, orders, and reports keep them, and you can restore them from the archive.';

export const formatCreatedDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTH_LABELS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
};

// Date + clock, for review rows inside a sub-day window — "added Aug 14, 2026"
// on every row of an hour-window review says nothing; the clock is the point.
export const formatCreatedInstant = (value) => {
  const day = formatCreatedDate(value);
  if (!day) return '';
  return `${day}, ${formatClockOf(new Date(value))}`;
};

/**
 * Say when a resolved created_at window was, in the user's local clock, reading
 * naturally after the word "added" — used for the latest-batch preview, where
 * only the client can put the server's instants into the user's timezone.
 * "today between 9:02 AM and 9:11 AM" / "yesterday at 4:12 PM" /
 * "on Aug 2, 2026 between 9:02 AM and 9:41 AM" /
 * "between Aug 13, 2026, 11:52 PM and Aug 14, 2026, 12:20 AM".
 */
export const describeInstantWindow = (fromIso, toIso, { now = new Date() } = {}) => {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  if (!sameLocalDay(from, to)) {
    return `between ${formatCreatedInstant(fromIso)} and ${formatCreatedInstant(toIso)}`;
  }
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const dayWord = sameLocalDay(from, now) ? 'today'
    : sameLocalDay(from, yesterday) ? 'yesterday'
      : `on ${formatCreatedDate(fromIso)}`;
  const t1 = formatClockOf(from);
  const t2 = formatClockOf(to);
  if (t1 === t2) return `${dayWord} at ${t1}`;
  return `${dayWord} between ${t1} and ${t2}`;
};
